import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Grid,
  List,
  ListItem,
  Card,
  CardMedia,
  CardContent,
  FormControlLabel,
  Checkbox,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import { Search, PhotoCamera } from '@mui/icons-material';
import SafeImage from './SafeImage';
import { popularFeeds, blogCategories } from '../data';

type Props = {
  searchTerm?: string;
  onSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedCategories?: string[];
  onCategoryChange?: (categorySlug: string) => void;
};

export default function BlogSidebar({ searchTerm, onSearch, selectedCategories, onCategoryChange }: Props) {
  const [localSearch, setLocalSearch] = useState('');
  const [localSelected, setLocalSelected] = useState<string[]>([]);

  const searchValue = typeof searchTerm === 'string' ? searchTerm : localSearch;
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSearch) return onSearch(e);
    setLocalSearch(e.target.value);
  };

  const selected = Array.isArray(selectedCategories) ? selectedCategories : localSelected;
  const handleCategoryChange = (slug: string) => {
    if (onCategoryChange) return onCategoryChange(slug);
    setLocalSelected(prev => (prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]));
  };

  return (
    <>
      <Grid item xs={12} md={4}>
        {/* Search */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Search
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Search posts..."
              value={searchValue}
              onChange={handleSearch}
              size="small"
            />
            <Button variant="contained" sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              <Search />
            </Button>
          </Box>
        </Paper>

        {/* Popular Posts */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Popular Posts
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {popularFeeds.map((feed) => (
              <Box
                key={feed.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                <SafeImage
                  src={`/assets/images/${feed.thumbnail}`}
                  alt={`${feed.title} - popular food post thumbnail`}
                  fallback={`/assets/images/fallback-${feed.id}.jpg`}
                  sx={{ width: 60, height: 60, borderRadius: 1 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                    {feed.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {feed.date} • {feed.readTime}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Categories */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Categories
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {blogCategories.map((category) => (
              <FormControlLabel
                key={category.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.includes(category.slug)}
                    onChange={() => handleCategoryChange(category.slug)}
                    sx={{ color: category.color, '&.Mui-checked': { color: category.color } }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
                    <Typography variant="body2">{category.name}</Typography>
                    <Typography variant="caption" color="text.secondary">({category.count})</Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        </Paper>

        {/* Recent Comments */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Recent Comments
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              { name: 'John Doe', comment: 'Amazing recipes! Love the blog.', time: '2 hours ago' },
              { name: 'Jane Smith', comment: 'The pizza guide was so helpful!', time: '5 hours ago' },
              { name: 'Mike Wilson', comment: "Can't wait to try the chicken recipe.", time: '1 day ago' },
            ].map((comment, index) => (
              <Box key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{comment.name.charAt(0)}</Avatar>
                  <Typography variant="body2" fontWeight={500}>{comment.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{comment.time}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>{comment.comment}</Typography>
                {index < 2 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Tags */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Tags</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              'fast-food', 'burgers', 'pizza', 'chicken', 'fries', 'sandwich', 'healthy', 'desserts', 'drinks', 'vegetarian',
            ].map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ borderColor: '#e0e0e0', color: '#666', '&:hover': { bgcolor: '#f5f5f5', borderColor: '#2e7d32', color: '#2e7d32' } }} />
            ))}
          </Box>
        </Paper>

        {/* Instagram Feed */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Instagram Feed</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PhotoCamera sx={{ color: '#e4405f' }} />
            <Typography variant="body2" color="text.secondary">@gudiyacooking</Typography>
          </Box>
          <Grid container spacing={1}>
            {[
              { name: 'pp1', ext: 'jpg' },
              { name: 'pp2', ext: 'jpg' },
              { name: 'pp3', ext: 'jpg' },
              { name: 'post-1', ext: 'jpg' },
              { name: 'post-2', ext: 'jpg' },
              { name: 'post-3', ext: 'jpg' },
            ].map((file) => (
              <Grid item xs={4} key={file.name}>
                <SafeImage src={`/assets/images/${file.name}.${file.ext}`} alt={`Gudiya Cooking Instagram ${file.name}`} fallback={`/assets/images/fallback-${file.name}.jpg`} sx={{ width: '100%', aspectRatio: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>
    </>
  );
}
